/** Dashboard section ids ↔ URL paths (nested under /dashboard). */
export const DASHBOARD_TAB_PATHS = {
  Overview: '/dashboard',
  AiStudio: '/ai-studio',
  CodeSandboxReview: '/dashboard/code-review',
  AiSpend: '/dashboard/ai-spend',
  TicketBriefings: '/dashboard/ticket-briefings',
  Notifications: '/dashboard/settings/notifications',
  Portfolio: '/dashboard/portfolio',
  Monitoring: '/dashboard/monitoring',
  Team: '/dashboard/team',
  Users: '/dashboard/users',
  Activity: '/dashboard/activity',
  Tickets: '/dashboard/tickets',
  ChatOperations: '/dashboard/chat',
  ChatSettings: '/dashboard/chat-settings',
  SprintPlanning: '/dashboard/sprint-planning',
  Kanban: '/dashboard/kanban',
  Links: '/dashboard/links',
  CommsOutreach: '/dashboard/communications/outreach',
  CrmMarketing: '/dashboard/crm',
  Workspace: '/dashboard/workspace',
  Profile: '/dashboard/profile',
  SettingsBilling: '/dashboard/settings/billing',
  Reports: '/dashboard/reports',
  GameStudio: '/dashboard/game-studio',
  ImageEditor: '/dashboard/image-editor',
  Files: '/dashboard/files',
};

/** Dashboard tab → Business App Hub `activeModules` key (if gated). */
export const TAB_ACTIVE_MODULE = {
  Portfolio: 'portfolio',
  Monitoring: 'uptimeMonitoring',
  Tickets: 'ticketing',
  ChatOperations: 'chatWidget',
  SprintPlanning: 'agileKanban',
  Kanban: 'agileKanban',
  CodeSandboxReview: 'codeReview',
  GameStudio: 'aiGameStudio',
  ImageEditor: 'aiGameStudio',
  AiStudio: 'aiStudio',
  Reports: 'reports',
  Files: 'files',
  CrmMarketing: 'crm',
  CommsOutreach: 'crm',
};

/** Dashboard tab → legacy `enabledModules` key (if gated). */
export const TAB_ENABLED_MODULE = {
  Tickets: 'ticketing',
  Links: 'externalLinks',
};

/** Path → activeModules key for direct URL guards. */
export const PATH_ACTIVE_MODULE = {
  ...Object.fromEntries(
    Object.entries(TAB_ACTIVE_MODULE).map(([tabId, moduleKey]) => [
      DASHBOARD_TAB_PATHS[tabId],
      moduleKey,
    ]),
  ),
  [DASHBOARD_TAB_PATHS.CodeSandboxReview]: 'codeReview',
};

/** Map legacy activeTab values from deep links to current section ids. */
export const LEGACY_TAB_ALIASES = {
  Alerts: 'Notifications',
  SettingsNotifications: 'Notifications',
  Sites: 'Monitoring',
  AppsSites: 'Portfolio',
  Team: 'Users',
};

/**
 * @param {string} tabId
 * @returns {string}
 */
export function dashboardPathForTab(tabId) {
  const normalized = LEGACY_TAB_ALIASES[tabId] || tabId;
  return DASHBOARD_TAB_PATHS[normalized] || DASHBOARD_TAB_PATHS.Overview;
}

/**
 * @param {string} ticketId
 * @returns {string}
 */
export function dashboardTicketPath(ticketId) {
  if (!ticketId || typeof ticketId !== 'string') {
    return DASHBOARD_TAB_PATHS.Tickets;
  }
  return `${DASHBOARD_TAB_PATHS.Tickets}/${encodeURIComponent(ticketId.trim())}`;
}

/**
 * @param {string} ticketId
 * @returns {string}
 */
export function dashboardCodeReviewPath(ticketId) {
  const base = DASHBOARD_TAB_PATHS.CodeSandboxReview;
  if (!ticketId || typeof ticketId !== 'string') return base;
  const trimmed = ticketId.trim();
  if (!trimmed) return base;
  return `${base}?ticketId=${encodeURIComponent(trimmed)}`;
}

/**
 * @param {string} [sessionId]
 * @returns {string}
 */
export function dashboardChatPath(sessionId) {
  const base = DASHBOARD_TAB_PATHS.ChatOperations;
  if (!sessionId || typeof sessionId !== 'string') return base;
  const trimmed = sessionId.trim();
  if (!trimmed) return base;
  return `${base}?sessionId=${encodeURIComponent(trimmed)}`;
}

/**
 * @param {string} pathname
 * @returns {string | null}
 */
export function parseTicketIdFromPathname(pathname) {
  const path = pathname.replace(/\/$/, '') || '';
  const match = path.match(/^\/dashboard\/tickets\/([^/]+)$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * @param {string} pathname
 * @returns {string | null}
 */
export function dashboardTabFromPathname(pathname) {
  const path = pathname.replace(/\/$/, '') || '/dashboard';
  if (parseTicketIdFromPathname(pathname)) return 'Tickets';
  if (path === '/dashboard/notifications') return 'Notifications';
  if (path === '/dashboard/team') return 'Users';
  if (path === '/ai-studio') return 'AiStudio';
  const match = Object.entries(DASHBOARD_TAB_PATHS).find(([, p]) => p === path);
  if (match) return match[0];
  if (path === '/dashboard') return 'Overview';
  return null;
}

/**
 * @param {string} tab
 * @returns {string}
 */
export function normalizeDashboardTab(tab) {
  return LEGACY_TAB_ALIASES[tab] || tab;
}
